// src/pages/Dashboard.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardAPI } from '../api/api';
import { useAuth } from '../context/AuthContext';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import toast from 'react-hot-toast';
import './Dashboard.css';

const MONTHS = ['','ಜನ','ಫೆಬ್','ಮಾರ್','ಏಪ್ರಿ','ಮೇ','ಜೂನ್','ಜುಲೈ','ಆಗ','ಸೆಪ್','ಅಕ್ಟೋ','ನವೆ','ಡಿಸೆ'];

export default function Dashboard() {
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  const { user }  = useAuth();
  const navigate  = useNavigate();
  const today     = new Date().toLocaleDateString('kn-IN', {
    weekday:'long', day:'numeric', month:'long', year:'numeric'
  });

  useEffect(() => {
    dashboardAPI.get()
      .then(r => setData(r.data.data))
      .catch(() => toast.error('ಡ್ಯಾಶ್‌ಬೋರ್ಡ್ ಲೋಡ್ ಆಗಲಿಲ್ಲ'))
      .finally(() => setLoading(false));
  }, []);

  const chartData = (data?.recent_days || [])
    .map(d => ({
      name: new Date(d.date).getDate() + ' ' + MONTHS[new Date(d.date).getMonth()+1],
      ಮಕ್ಕಳು: d.students,
      ಖರ್ಚು: parseFloat(d.cost).toFixed(0),
    })).reverse();

  if (loading) return (
    <div className="dash-loading">
      <div className="dash-spinner" />
      <p>ಲೋಡ್ ಆಗುತ್ತಿದೆ...</p>
    </div>
  );

  return (
    <div className="dashboard">
      {/* Welcome */}
      <div className="dash-welcome">
        <div>
          <h1 className="dash-welcome-title">
            ನಮಸ್ಕಾರ, {user?.full_name_kannada?.split(' ')[0]}! 👋
          </h1>
          <p className="dash-date">{today}</p>
        </div>
        <button className="dash-attendance-btn" onClick={() => navigate('/attendance')}>
          ✅ ಹಾಜರಾತಿ ನಮೂದಿಸಿ
        </button>
      </div>

      {/* Stats cards */}
      <div className="dash-stats">
        <StatCard
          icon="👨‍🎓" title="ಇಂದು ಮಕ್ಕಳು" color="green"
          value={data?.today?.students ?? '—'}
          sub="ಊಟ ಉಣಿಸಿದ ಮಕ್ಕಳ ಸಂಖ್ಯೆ"
        />
        <StatCard
          icon="💰" title="ಇಂದಿನ ಖರ್ಚು" color="blue"
          value={data?.today?.cost ? `₹${parseFloat(data.today.cost).toFixed(2)}` : '—'}
          sub="ಒಟ್ಟು ಆಹಾರ ವೆಚ್ಚ"
        />
        <StatCard
          icon="📅" title="ತಿಂಗಳ ಕಾರ್ಯದಿನ" color="amber"
          value={data?.this_month?.working_days ?? '—'}
          sub="ಈ ತಿಂಗಳ ಶಾಲೆ ದಿನಗಳು"
        />
        <StatCard
          icon="📊" title="ತಿಂಗಳ ಒಟ್ಟು ಖರ್ಚು" color="purple"
          value={data?.this_month?.total_cost ? `₹${parseFloat(data.this_month.total_cost).toFixed(0)}` : '—'}
          sub="ಈ ತಿಂಗಳ ಒಟ್ಟು ವೆಚ್ಚ"
        />
      </div>

      <div className="dash-grid-2">
        {/* Chart */}
        <div className="dash-card">
          <h2 className="card-title">📈 ಕಳೆದ ೭ ದಿನ - ಮಕ್ಕಳ ಸಂಖ್ಯೆ</h2>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={chartData} margin={{ top:10, right:10, left:-10, bottom:0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="name" tick={{ fill:'rgba(255,255,255,0.5)', fontSize:11 }} />
                <YAxis tick={{ fill:'rgba(255,255,255,0.5)', fontSize:11 }} />
                <Tooltip
                  contentStyle={{
                    background:'rgba(8,15,30,0.95)', border:'1px solid rgba(74,222,128,0.3)',
                    borderRadius:'10px', fontFamily:'Noto Sans Kannada',
                  }}
                  labelStyle={{ color:'#86efac' }}
                  itemStyle={{ color:'#fff' }}
                />
                <Bar dataKey="ಮಕ್ಕಳು" fill="#22c55e" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="chart-empty">ಇನ್ನೂ ಮಾಹಿತಿ ಇಲ್ಲ</div>
          )}
        </div>

        {/* Alerts + month summary */}
        <div className="dash-right-col">
          {/* Month summary */}
          <div className="dash-card">
            <h2 className="card-title">🗓️ ತಿಂಗಳ ಸಾರಾಂಶ</h2>
            <div className="month-summary-list">
              <SummaryRow label="ಊಟ ಉಣಿಸಿದ ಮಕ್ಕಳು"
                value={data?.this_month?.students_fed?.toLocaleString('kn-IN') ?? '—'} />
              <SummaryRow label="ಕಾರ್ಯ ದಿನಗಳು"
                value={data?.this_month?.working_days ?? '—'} />
              <SummaryRow label="ಒಟ್ಟು ವೆಚ್ಚ"
                value={data?.this_month?.total_cost ? `₹${parseFloat(data.this_month.total_cost).toFixed(2)}` : '—'} />
            </div>
          </div>

          {/* Low stock alerts */}
          {data?.low_stock_alerts?.length > 0 && (
            <div className="dash-card dash-alert-card">
              <h2 className="card-title">⚠️ ಕಡಿಮೆ ಸ್ಟಾಕ್ ಎಚ್ಚರಿಕೆ</h2>
              {data.low_stock_alerts.map((a, i) => (
                <div key={i} className="alert-row">
                  <span className="alert-dot" />
                  <span className="alert-name">{a.name_kannada}</span>
                  <span className="alert-qty">
                    {parseFloat(a.closing_stock).toFixed(2)} {a.unit_label_kannada}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Quick actions */}
      <div className="dash-quick-actions">
        <h2 className="card-title" style={{marginBottom:'1rem'}}>⚡ ತ್ವರಿತ ಕ್ರಿಯೆಗಳು</h2>
        <div className="quick-grid">
          {[
            { icon:'✅', label:'ಹಾಜರಾತಿ', path:'/attendance', color:'green' },
            { icon:'📋', label:'ವರದಿ',     path:'/reports',    color:'blue' },
            { icon:'🌾', label:'ಸ್ಟಾಕ್',    path:'/stock',      color:'amber' },
            { icon:'👥', label:'ಸಂದರ್ಶನ',  path:'/visitors',   color:'purple' },
          ].map(q => (
            <button key={q.path} className={`quick-btn quick-${q.color}`}
              onClick={() => navigate(q.path)}>
              <span className="quick-icon">{q.icon}</span>
              <span className="quick-label">{q.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon, title, value, sub, color }) {
  return (
    <div className={`stat-card stat-${color}`}>
      <div className="stat-icon">{icon}</div>
      <div className="stat-body">
        <div className="stat-title">{title}</div>
        <div className="stat-value">{value}</div>
        <div className="stat-sub">{sub}</div>
      </div>
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div className="summary-row">
      <span className="summary-label">{label}</span>
      <span className="summary-value">{value}</span>
    </div>
  );
}
